// PersonalAssistantSection.tsx — Karte „Persönlicher Assistent“ in der Kontaktanlage.
// Bietet Schnellauswahl bestehender Kontakte ODER direkte Eingabe von:
// Anrede, Titel, Nachname, Vorname, Telefon, Mobil, E-Mail, Aufgabe
// sowie weitere Kommunikationswege über den WeitereKommunikationDialog.

import * as React from "react";
import { FieldRow, LabeledInput, LabeledSelect } from "../forms/formPrimitives";
import { PhoneInput } from "../forms/widgets/PhoneInput";
import { EmailInput } from "../forms/widgets/EmailInput";
import {
  WeitereKommunikationDialog,
  type WeitereKommunikationEintrag,
} from "../forms/widgets/WeitereKommunikationDialog";
import { useAnredeOptionen } from "../forms/useAnredeOptionen";
import { REFINED } from "../risikoanalyseStep/tokens";
import { apiUrl } from "../../utils/api";
import { useRelStyles } from "./styles";
import { uid, assistantToRef, type AssistantItem } from "./types";

const TITLE_OPTIONS = [
  { value: "Dr.", label: "Dr." },
  { value: "Prof.", label: "Prof." },
  { value: "Prof. Dr.", label: "Prof. Dr." },
];

const SearchIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
  >
    <circle cx="9" cy="9" r="6" />
    <path d="M14 14l3.5 3.5" />
  </svg>
);

function createEmptyAssistant(seed?: Partial<AssistantItem>): AssistantItem {
  return {
    id: seed?.id ?? uid(),
    ref: null,
    anrede: "",
    title: "",
    lastName: "",
    firstName: "",
    phone: "",
    mobile: "",
    email: "",
    extraCommunications: [],
    ...seed,
  };
}

export function PersonalAssistantSection({
  value,
  onChange,
  idBase = "ast",
}: {
  value: AssistantItem[];
  onChange: (items: AssistantItem[]) => void;
  idBase?: string;
}) {
  const s = useRelStyles();
  const anredeOptionen = useAnredeOptionen();

  // Ist die übergebene Liste leer, wird 1 leeres Formular gerendert,
  // sodass die Eingabefelder direkt sichtbar sind.
  const items = React.useMemo(() => {
    return value.length > 0 ? value : [createEmptyAssistant({ id: `${idBase}-1` })];
  }, [value, idBase]);

  const [dialogTargetId, setDialogTargetId] = React.useState<string | null>(null);

  // Suche je Item
  const [searchQueries, setSearchQueries] = React.useState<Record<string, string>>({});
  const [searchResults, setSearchResults] = React.useState<Record<string, any[]>>({});
  const [isSearching, setIsSearching] = React.useState<Record<string, boolean>>({});

  const updateItem = (id: string, patch: Partial<AssistantItem>) => {
    const next = items.map((it) => {
      if (it.id !== id) return it;
      const merged = { ...it, ...patch };
      return {
        ...merged,
        ref: assistantToRef(merged),
      };
    });
    onChange(next);
  };

  const clearItem = (id: string) => {
    if (value.length > 1) {
      onChange(value.filter((it) => it.id !== id));
    } else {
      onChange([]);
    }
  };

  const addAssistant = () => {
    const fresh = createEmptyAssistant();
    onChange([...value, fresh]);
  };

  const handleSearchChange = (itemId: string, query: string) => {
    setSearchQueries((prev) => ({ ...prev, [itemId]: query }));
    const term = query.trim();
    if (term.length < 2) {
      setSearchResults((prev) => ({ ...prev, [itemId]: [] }));
      return;
    }

    setIsSearching((prev) => ({ ...prev, [itemId]: true }));
    fetch(apiUrl("contact", { search: term, limit: 50 }))
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        const persons = list.filter((c: any) => c?.type?.type !== "Firma").slice(0, 8);
        setSearchResults((prev) => ({ ...prev, [itemId]: persons }));
      })
      .catch(() => {
        setSearchResults((prev) => ({ ...prev, [itemId]: [] }));
      })
      .finally(() => {
        setIsSearching((prev) => ({ ...prev, [itemId]: false }));
      });
  };

  const selectContact = (itemId: string, contact: any) => {
    const phone =
      contact?.phone ||
      contact?.communications?.find((c: any) => c.type === "phone")?.value ||
      "";
    const mobile =
      contact?.mobile ||
      contact?.communications?.find((c: any) => c.type === "mobile")?.value ||
      "";
    const email =
      contact?.email ||
      contact?.communications?.find((c: any) => c.type === "email")?.value ||
      "";

    updateItem(itemId, {
      contactId: contact.id,
      anrede: contact.salutation?.salutation || "",
      title: contact.title?.title || "",
      lastName: contact.lastname || "",
      firstName: contact.firstName || "",
      phone,
      mobile,
      email,
    });

    setSearchQueries((prev) => ({ ...prev, [itemId]: "" }));
    setSearchResults((prev) => ({ ...prev, [itemId]: [] }));
  };

  const addExtraComm = (itemId: string, entry: WeitereKommunikationEintrag) => {
    const target = items.find((it) => it.id === itemId);
    if (!target) return;
    const existing = target.extraCommunications || [];
    updateItem(itemId, { extraCommunications: [...existing, entry] });
  };

  const updateExtraComm = (
    itemId: string,
    commId: string,
    patch: Partial<WeitereKommunikationEintrag>
  ) => {
    const target = items.find((it) => it.id === itemId);
    if (!target) return;
    const nextComms = (target.extraCommunications || []).map((c) =>
      c.id === commId ? { ...c, ...patch } : c
    );
    updateItem(itemId, { extraCommunications: nextComms });
  };

  const removeExtraComm = (itemId: string, commId: string) => {
    const target = items.find((it) => it.id === itemId);
    if (!target) return;
    const nextComms = (target.extraCommunications || []).filter((c) => c.id !== commId);
    updateItem(itemId, { extraCommunications: nextComms });
  };

  return (
    <div className={s.list}>
      {items.map((it, idx) => {
        const q = searchQueries[it.id] ?? "";
        const results = searchResults[it.id] ?? [];
        const searching = isSearching[it.id] ?? false;
        const hasData = Boolean(
          it.contactId ||
            it.lastName?.trim() ||
            it.firstName?.trim() ||
            it.phone?.trim() ||
            it.mobile?.trim() ||
            it.email?.trim() ||
            it.aufgabe?.trim() ||
            (it.extraCommunications && it.extraCommunications.length > 0)
        );

        return (
          <React.Fragment key={it.id}>
            {idx > 0 && <hr className={s.divider} />}

            {/* Optionaler Kontaktbezug: Entweder verknüpfter Kontakt oder Schnellsuchfeld */}
            {it.contactId ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  backgroundColor: REFINED.fieldBg,
                  border: `1px solid ${REFINED.line}`,
                  borderRadius: "8px",
                  marginBottom: "12px",
                }}
              >
                <span style={{ fontSize: "0.86rem", color: REFINED.ink, fontWeight: 600 }}>
                  Bestehender Kontakt verknüpft: {it.firstName ? `${it.firstName} ` : ""}
                  {it.lastName} (ID {it.contactId})
                </span>
                <button
                  type="button"
                  onClick={() => updateItem(it.id, { contactId: undefined })}
                  className={s.ghostBtn}
                  style={{ fontSize: "0.8rem", padding: "4px 8px" }}
                >
                  Verknüpfung lösen
                </button>
              </div>
            ) : (
              <div className={s.refField} style={{ marginBottom: "14px" }}>
                <div className={s.searchWrap}>
                  <span className={s.searchIcon}>{SearchIcon}</span>
                  <input
                    className={s.searchInput}
                    placeholder="Bestehenden Kontakt als Assistenz suchen (optional) …"
                    value={q}
                    onChange={(e) => handleSearchChange(it.id, e.target.value)}
                  />
                </div>
                {searching && <div className={s.msg}>Suche …</div>}
                {!searching && q.trim().length >= 2 && results.length === 0 && (
                  <div className={s.msg}>Keine Treffer.</div>
                )}
                {results.length > 0 && (
                  <div className={s.results}>
                    {results.map((c) => {
                      const dName =
                        c.displayName ||
                        `${c.lastname ?? ""}${c.firstName ? ", " + c.firstName : ""}`.trim() ||
                        "—";
                      return (
                        <button
                          type="button"
                          key={c.id}
                          className={s.resultRow}
                          onClick={() => selectContact(it.id, c)}
                        >
                          <span className={s.resultName}>{dName}</span>
                          <span className={s.resultType}>Privatperson</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Zeile 1: Anrede | Titel */}
            <FieldRow columns={2}>
              <LabeledSelect
                label="Anrede"
                name={`ast-anrede-${it.id}`}
                value={it.anrede ?? ""}
                onChange={(v) => updateItem(it.id, { anrede: v })}
                options={[
                  { value: "", label: "— Bitte wählen —" },
                  ...anredeOptionen.map((o) => ({ value: o.salutation, label: o.salutation })),
                ]}
                placeholder="Auswählen"
              />
              <LabeledSelect
                label="Titel"
                name={`ast-title-${it.id}`}
                value={it.title ?? ""}
                onChange={(v) => updateItem(it.id, { title: v })}
                options={[{ value: "", label: "— Kein Titel —" }, ...TITLE_OPTIONS]}
                placeholder="Auswählen"
              />
            </FieldRow>

            {/* Zeile 2: Nachname | Vorname */}
            <FieldRow columns={2}>
              <LabeledInput
                label="Nachname"
                name={`ast-lastname-${it.id}`}
                value={it.lastName ?? ""}
                onChange={(v) => updateItem(it.id, { lastName: v })}
                placeholder="Mustermann"
              />
              <LabeledInput
                label="Vorname"
                name={`ast-firstname-${it.id}`}
                value={it.firstName ?? ""}
                onChange={(v) => updateItem(it.id, { firstName: v })}
                placeholder="Max"
              />
            </FieldRow>

            {/* Zeile 3: Telefon | Mobil */}
            <FieldRow columns={2}>
              <PhoneInput
                label="Telefon"
                name={`ast-phone-${it.id}`}
                kind="landline"
                value={it.phone ?? ""}
                onChange={(v) => updateItem(it.id, { phone: v })}
                geschaeftlich
              />
              <PhoneInput
                label="Mobil"
                name={`ast-mobile-${it.id}`}
                kind="mobile"
                value={it.mobile ?? ""}
                onChange={(v) => updateItem(it.id, { mobile: v })}
                geschaeftlich
              />
            </FieldRow>

            {/* Zeile 4: E-Mail | Aufgabe */}
            <FieldRow columns={2}>
              <EmailInput
                label="E-Mail"
                name={`ast-email-${it.id}`}
                value={it.email ?? ""}
                onChange={(v) => updateItem(it.id, { email: v })}
              />
              <LabeledInput
                label="Aufgabe"
                name={`ast-aufgabe-${it.id}`}
                value={it.aufgabe ?? ""}
                onChange={(v) => updateItem(it.id, { aufgabe: v })}
                placeholder="z. B. Terminkoordination, Post, Reisen"
              />
            </FieldRow>

            {/* Zeile 5+: Weitere Kommunikationswege (genau wie in der Karte Kommunikation) */}
            {it.extraCommunications && it.extraCommunications.length > 0 && (
              <FieldRow columns={2}>
                {it.extraCommunications.map((extra) => {
                  const commTitle = extra.label?.trim()
                    ? extra.label.trim()
                    : extra.type === "mobile"
                      ? "Mobil (Zusatz)"
                      : extra.type === "email"
                        ? "E-Mail (Zusatz)"
                        : extra.type === "fax"
                          ? "Fax"
                          : "Telefon (Zusatz)";

                  return (
                    <div key={extra.id} style={{ display: "flex", alignItems: "flex-end", gap: "8px" }}>
                      <div style={{ flex: 1 }}>
                        {extra.type === "email" ? (
                          <EmailInput
                            label={commTitle}
                            name={`extra-comm-${extra.id}`}
                            value={extra.value}
                            onChange={(v) => updateExtraComm(it.id, extra.id, { value: v })}
                          />
                        ) : (
                          <PhoneInput
                            label={commTitle}
                            name={`extra-comm-${extra.id}`}
                            kind={extra.type === "fax" ? "fax" : extra.type === "mobile" ? "mobile" : "landline"}
                            value={extra.value}
                            onChange={(v) => updateExtraComm(it.id, extra.id, { value: v })}
                            geschaeftlich
                          />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeExtraComm(it.id, extra.id)}
                        className={s.removeBtn}
                        style={{ height: "40px", marginBottom: "8px" }}
                        title="Kommunikationsweg entfernen"
                      >
                        Entfernen
                      </button>
                    </div>
                  );
                })}
              </FieldRow>
            )}

            <div className={s.rowActions} style={{ marginTop: "8px" }}>
              <button
                type="button"
                className={s.ghostBtn}
                onClick={() => setDialogTargetId(it.id)}
              >
                + Weiterer Kommunikationsweg
              </button>
            </div>
            {(hasData || items.length > 1) && (
              <div className={s.itemTopBar} style={{ marginTop: "4px" }}>
                <button
                  type="button"
                  className={s.removeBtn}
                  onClick={() => clearItem(it.id)}
                  title={items.length > 1 ? "Assistenten entfernen" : "Angaben leeren"}
                >
                  {items.length > 1 ? "Entfernen" : "Angaben leeren"}
                </button>
              </div>
            )}
          </React.Fragment>
        );
      })}

      <div className={s.rowActions} style={{ marginTop: "16px", paddingTop: "8px", borderTop: `1px solid ${REFINED.line}` }}>
        <button type="button" className={s.ghostBtn} onClick={addAssistant}>
          + Weiteren Assistenten hinzufügen
        </button>
      </div>

      <WeitereKommunikationDialog
        open={dialogTargetId !== null}
        onClose={() => setDialogTargetId(null)}
        showZuordnung={false}
        defaultZuordnung="geschaeftlich"
        onSave={(entry) => {
          if (dialogTargetId) {
            addExtraComm(dialogTargetId, entry);
          }
          setDialogTargetId(null);
        }}
        onAdd={(entry) => {
          if (dialogTargetId) {
            addExtraComm(dialogTargetId, entry);
          }
          setDialogTargetId(null);
        }}
      />
    </div>
  );
}
